import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { startCase } from "lodash";
import { CreditCard, Eye } from "lucide-react";
import { Link } from "react-router-dom";

import { useGetCardWalletQuery } from "@/services/(my-space)/api.documents";
import { PATHS } from "@/routes/paths";
import { DashboardCard } from "@/components/common/DashboardCard";
import { cn } from "@/utils/cn";
import { WalletViewDialog } from "./WalletViewDialog";

export const cardFallbackImage = "/images/wallet-card-fallback.svg";

const STACK_HEIGHT = 280;
const MAX_PEEK = 48;
const PRIMARY = "100,56,255";

type WalletCard = {
  uid: string;
  label: string;
  frontImageUrl: string | null;
  backImageUrl: string | null;
};

export const DashboardWalletWidget = ({
  className,
}: {
  className?: string;
}) => {
  const { t } = useTranslation("my-space");

  const { data: walletData, isFetching } = useGetCardWalletQuery();

  const [reorderedUids, setReorderedUids] = useState<string[] | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [previewCard, setPreviewCard] = useState<WalletCard | null>(null);

  const allCards = useMemo<WalletCard[]>(() => {
    const docs = (walletData?.data?.documents ?? []).map((d) => ({
      uid: `doc-${d.id}`,
      label: d.documentType ?? "",
      frontImageUrl: d.frontSideDocumentUrl,
      backImageUrl: d.backSideDocumentUrl,
    }));
    const assets = (walletData?.data?.assets ?? []).map((a) => ({
      uid: `asset-${a.id}`,
      label: a.assetType?.name ?? "",
      frontImageUrl: a.primaryImage?.fileUrl ?? null,
      backImageUrl: null,
    }));
    return [...docs, ...assets];
  }, [walletData]);

  const orderedCards = useMemo(() => {
    if (!allCards.length) return [];
    if (!reorderedUids) return allCards;
    return reorderedUids
      .map((uid) => allCards.find((c) => c.uid === uid))
      .filter((c): c is WalletCard => Boolean(c));
  }, [allCards, reorderedUids]);

  const cardCount = orderedCards.length;
  const peek =
    cardCount > 1
      ? Math.min(MAX_PEEK, Math.floor((STACK_HEIGHT - 130) / (cardCount - 1)))
      : MAX_PEEK;
  const cardHeight =
    cardCount > 1 ? STACK_HEIGHT - (cardCount - 1) * peek : STACK_HEIGHT;

  const bringToFront = (uid: string) => {
    setHoveredIndex(null);
    setReorderedUids((prev) => {
      const base = prev ?? orderedCards.map((c) => c.uid);
      return [uid, ...base.filter((u) => u !== uid)];
    });
  };

  const headerRight = (
    <div className="flex items-center gap-3">
      {!isFetching && cardCount > 0 && (
        <span
          className="text-[11px] font-bold px-2.5 py-0.5 rounded-full tabular-nums"
          style={{
            background: `rgba(${PRIMARY},0.1)`,
            color: `rgb(${PRIMARY})`,
          }}
        >
          {cardCount}{" "}
          {cardCount === 1 ? t("dashboard.card_one") : t("dashboard.card_many")}
        </span>
      )}
      <Link
        to={PATHS.MY_SPACE.MS_DOCUMENTS_PATHS.ROOT}
        className="text-sm font-semibold transition-colors"
        style={{ color: `rgb(${PRIMARY})` }}
      >
        {t("dashboard.view_all_arrow")}
      </Link>
    </div>
  );

  return (
    <>
      <DashboardCard
        icon="/icons/interface-icons/stats/payroll/icon-3d-card-settings.png"
        title={t("dashboard.card_wallet")}
        rootClassname={className}
        contentClassName="overflow-hidden"
        headerRight={headerRight}
      >
        {isFetching ? (
          <StackSkeleton
            peek={MAX_PEEK}
            cardHeight={STACK_HEIGHT - 2 * MAX_PEEK}
          />
        ) : !cardCount ? (
          <EmptyState />
        ) : (
          <div
            className="rounded-2xl p-4 flex flex-col gap-4"
            style={{
              background: `linear-gradient(160deg, rgba(${PRIMARY},0.06) 0%, rgba(${PRIMARY},0.11) 100%)`,
              border: `1px solid rgba(${PRIMARY},0.12)`,
            }}
          >
            {/* ── Card stack ── */}
            <div className="relative" style={{ height: `${STACK_HEIGHT}px` }}>
              {/* Ambient glow under stack */}
              <div
                className="absolute bottom-0 left-1/2 pointer-events-none"
                style={{
                  transform: "translateX(-50%)",
                  width: "75%",
                  height: 32,
                  borderRadius: "50%",
                  background: `rgba(${PRIMARY},0.25)`,
                  filter: "blur(20px)",
                  zIndex: 0,
                }}
              />

              {orderedCards.map((card, index) => {
                const isFront = index === 0;
                const isHovered = hoveredIndex === index;
                const scaleX = isFront ? 1 : 1 - index * 0.03;
                const translateY = isHovered ? (isFront ? -8 : -36) : 0;
                const scaleUniform = isHovered && isFront ? 1.018 : 1;

                const shadow = isFront
                  ? isHovered
                    ? `0 28px 60px rgba(0,0,0,0.28),
                       0 0 0 2px white,
                       0 0 0 4.5px rgba(${PRIMARY},0.6),
                       0 0 36px 6px rgba(${PRIMARY},0.22)`
                    : `0 12px 40px rgba(0,0,0,0.2),
                       0 0 0 2px white,
                       0 0 0 4px rgba(${PRIMARY},0.38)`
                  : `0 ${5 + index * 4}px ${18 + index * 10}px rgba(0,0,0,${0.08 + index * 0.05}),
                     0 0 0 1.5px rgba(255,255,255,0.75)`;

                return (
                  <div
                    key={card.uid}
                    className="absolute start-0 end-0 overflow-hidden rounded-2xl cursor-pointer select-none"
                    style={{
                      bottom: `${index * peek}px`,
                      zIndex: cardCount - index + 1,
                      height: `${cardHeight}px`,
                      transform: `scaleX(${scaleX}) scale(${scaleUniform}) translateY(${translateY}px)`,
                      boxShadow: shadow,
                      transition: [
                        "transform 360ms cubic-bezier(0.34,1.4,0.64,1)",
                        "bottom   420ms cubic-bezier(0.34,1.2,0.64,1)",
                        "box-shadow 300ms ease",
                      ].join(", "),
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={() =>
                      isFront ? setPreviewCard(card) : bringToFront(card.uid)
                    }
                  >
                    {/* Card image */}
                    <img
                      src={card.frontImageUrl ?? cardFallbackImage}
                      alt={card.label || t("common:payment_methods.card")}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src =
                          cardFallbackImage;
                      }}
                    />

                    {/* Shine overlay on all cards */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 55%)",
                      }}
                    />

                    {/* Back card depth scrim */}
                    {!isFront && (
                      <div
                        className="absolute inset-0 pointer-events-none rounded-2xl"
                        style={{ background: `rgba(12,6,32,${index * 0.1})` }}
                      />
                    )}

                    {/* Back card peek strip */}
                    {!isFront && (
                      <div
                        className="absolute top-0 start-0 end-0 flex items-center justify-between gap-2 px-4 pointer-events-none rounded-t-2xl overflow-hidden"
                        style={{
                          height: `${peek}px`,
                          background:
                            "linear-gradient(to bottom, rgba(0,0,0,0.35), transparent)",
                          backdropFilter: "blur(2px)",
                        }}
                      >
                        <span className="text-white text-[11px] font-semibold drop-shadow-sm truncate">
                          {startCase(card.label)}
                        </span>
                        <span
                          className="text-[9px] font-semibold uppercase tracking-widest shrink-0 px-2 py-0.5 rounded-full"
                          style={{
                            background: "rgba(255,255,255,0.15)",
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          {t("dashboard.tap")}
                        </span>
                      </div>
                    )}

                    {/* Front card overlay */}
                    {isFront && (
                      <div className="absolute inset-0 pointer-events-none flex flex-col justify-end">
                        <div
                          className="px-4 pb-4 pt-12"
                          style={{
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)",
                          }}
                        >
                          <div className="flex items-end justify-between gap-2">
                            <span className="text-white text-[13px] font-bold leading-tight truncate drop-shadow-sm">
                              {startCase(card.label)}
                            </span>
                            <div
                              className="flex items-center gap-1.5 text-white text-[10px] font-bold rounded-full px-3 py-1.5 shrink-0"
                              style={{
                                background: `rgba(${PRIMARY},0.75)`,
                                backdropFilter: "blur(8px)",
                                boxShadow: `0 2px 12px rgba(${PRIMARY},0.4)`,
                                opacity: isHovered ? 1 : 0,
                                transform: `translateY(${isHovered ? 0 : 8}px)`,
                                transition:
                                  "opacity 180ms ease, transform 200ms cubic-bezier(0.34,1.4,0.64,1)",
                              }}
                            >
                              <Eye className="w-3 h-3" />
                              {t("labels.view")}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Card selector tabs ── */}
            {cardCount > 1 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
                {orderedCards.map((card, i) => {
                  const isActive = i === 0;
                  return (
                    <button
                      key={card.uid}
                      onClick={() => !isActive && bringToFront(card.uid)}
                      title={startCase(card.label)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold whitespace-nowrap transition-all duration-300 shrink-0",
                        isActive ? "cursor-default" : "cursor-pointer",
                      )}
                      style={
                        isActive
                          ? {
                              background: `rgba(${PRIMARY},0.12)`,
                              borderColor: `rgba(${PRIMARY},0.4)`,
                              color: `rgb(${PRIMARY})`,
                              boxShadow: `0 0 0 1px rgba(${PRIMARY},0.2)`,
                            }
                          : {
                              background: "rgba(255,255,255,0.5)",
                              borderColor: "rgba(0,0,0,0.07)",
                              color: "var(--color-muted-foreground)",
                            }
                      }
                    >
                      {/* Mini thumbnail */}
                      <div
                        className="overflow-hidden rounded-sm shrink-0"
                        style={{ width: 24, height: 15 }}
                      >
                        <img
                          src={card.frontImageUrl ?? cardFallbackImage}
                          alt=""
                          className="w-full h-full object-cover object-top"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src =
                              cardFallbackImage;
                          }}
                        />
                      </div>
                      <span className="truncate max-w-[80px]">
                        {startCase(card.label)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </DashboardCard>

      <WalletViewDialog
        isOpen={Boolean(previewCard)}
        onClose={() => setPreviewCard(null)}
        title={previewCard?.label ?? ""}
        frontImage={previewCard?.frontImageUrl ?? null}
        backImage={previewCard?.backImageUrl ?? null}
      />
    </>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const StackSkeleton = ({
  peek,
  cardHeight,
}: {
  peek: number;
  cardHeight: number;
}) => (
  <div
    className="rounded-2xl border p-4 flex flex-col gap-4 animate-pulse"
    style={{
      background: `linear-gradient(160deg, rgba(${PRIMARY},0.04) 0%, rgba(${PRIMARY},0.07) 100%)`,
      borderColor: `rgba(${PRIMARY},0.08)`,
    }}
  >
    <div className="relative" style={{ height: `${STACK_HEIGHT}px` }}>
      {[2, 1, 0].map((index) => (
        <div
          key={index}
          className="absolute start-0 end-0 rounded-2xl"
          style={{
            bottom: `${(2 - index) * peek}px`,
            height: `${cardHeight}px`,
            zIndex: index + 1,
            background:
              index === 2
                ? "rgba(0,0,0,0.06)"
                : index === 1
                  ? "rgba(0,0,0,0.04)"
                  : "var(--color-border)",
            transform: `scaleX(${1 - (2 - index) * 0.03})`,
          }}
        />
      ))}
    </div>
    <div className="flex items-center gap-2">
      {[80, 64, 64].map((w, i) => (
        <div
          key={i}
          className="h-8 rounded-xl"
          style={{ width: w, background: "rgba(0,0,0,0.06)" }}
        />
      ))}
    </div>
  </div>
);

const EmptyState = () => {
  const { t } = useTranslation("my-space");

  return (
    <div
      className="rounded-2xl border flex flex-col items-center justify-center gap-4 py-10"
      style={{
        background: `linear-gradient(160deg, rgba(${PRIMARY},0.04) 0%, rgba(${PRIMARY},0.08) 100%)`,
        borderColor: `rgba(${PRIMARY},0.15)`,
        borderStyle: "dashed",
      }}
    >
      <div
        className="rounded-2xl p-4"
        style={{
          background: `rgba(${PRIMARY},0.08)`,
          boxShadow: `0 0 0 8px rgba(${PRIMARY},0.05)`,
        }}
      >
        <CreditCard
          className="w-8 h-8"
          style={{ color: `rgba(${PRIMARY},0.5)` }}
        />
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-foreground/60">
          {t("labels.no_cards_yet")}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("labels.cards_appear_here")}
        </p>
      </div>
    </div>
  );
};
